import type {CustomScrollbar} from '@/components/custom-scrollbar/custom-scrollbar';

interface ZoomOptions {
    min?: number;
    max?: number;
    step?: number;
    initialScale?: number;
    disabledClass?: string;
}

/**
 * Класс контроллера зума для управления масштабированием контента
 * @class ZoomController
 */
export class ZoomController {
    /**
     * Основной контейнер-обертка
     * @private
     * @type {HTMLElement}
     */
    private readonly wrapper: HTMLElement;

    /**
     * Элемент контента, который масштабируется
     * @private
     * @type {HTMLElement}
     */
    private readonly content: HTMLElement;

    /**
     * Элемент скроллера (прокручиваемая область)
     * @private
     * @type {HTMLElement}
     */
    private readonly scroller: HTMLElement;

    /**
     * Кнопки управления зумом
     * @private
     * @type {Object}
     * @property {HTMLElement|null} zoomIn - Кнопка увеличения масштаба
     * @property {HTMLElement|null} zoomOut - Кнопка уменьшения масштаба
     * @property {HTMLElement|null} reset - Кнопка сброса масштаба
     */
    private readonly zoomButtons: {
        zoomIn: HTMLElement | null;
        zoomOut: HTMLElement | null;
        reset: HTMLElement | null;
    };

    /**
     * Экземпляр кастомного скроллбара
     * @private
     * @type {CustomScrollbar}
     */
    private scrollbar: CustomScrollbar;

    /**
     * Текущее значение масштаба
     * @private
     * @type {number}
     * @default 1
     */
    private scale: number = 1;

    /**
     * Минимальное значение масштаба
     * @private
     * @type {number}
     */
    private readonly min: number;

    /**
     * Максимальное значение масштаба
     * @private
     * @type {number}
     */
    private readonly max: number;

    /**
     * Шаг изменения масштаба
     * @private
     * @type {number}
     */
    private readonly step: number;

    /**
     * CSS-класс для отключенных кнопок
     * @private
     * @type {string}
     */
    private readonly disabledClass: string;

    constructor(wrapper: HTMLElement, options: ZoomOptions = {}) {
        this.wrapper = wrapper;
        this.content = wrapper.querySelector('.j-custom-scrollable-el')!;
        this.scroller = wrapper.querySelector('div')!;
        this.zoomButtons = {
            zoomIn : null,
            zoomOut: null,
            reset  : null
        };

        this.min = options.min ?? 0.7;
        this.max = options.max ?? 3;
        this.step = options.step ?? 0.4;
        this.disabledClass = options.disabledClass ?? 'disabled';

        this.init();
        this.initZoomButtons();
        this.updateButtonsState();

        const initialScale = options.initialScale ?? 1;

        this.setScale(initialScale);
    }

    /**
     * Увеличивает масштаб на один шаг
     */
    zoomIn(): void {
        this.setScale(this.scale + this.step);
    }

    /**
     * Уменьшает масштаб на один шаг
     */
    zoomOut(): void {
        this.setScale(this.scale - this.step);
    }

    /**
     * Сбрасывает масштаб к начальному значению
     */
    reset(): void {
        this.setScale(1);
    }

    /**
     * Устанавливает экземпляр кастомного скроллбара
     * @param {CustomScrollbar} scrollbar - Экземпляр кастомного скроллбара
     */
    setScrollbar(scrollbar: CustomScrollbar): void {
        this.scrollbar = scrollbar;
        this.scrollbar.setScale(this.scale);
    }

    /**
     * Инициализирует начальные настройки
     */
    private init(): void {
        this.content.style.transformOrigin = '0 0';
        this.content.style.willChange = 'transform';
    }

    /**
     * Устанавливает новое значение масштаба
     * @param {number} value - Новое значение масштаба
     */
    private setScale(value: number): void {
        const newScale = Math.min(this.max, Math.max(this.min, value));

        if (newScale === this.scale) {
            return;
        }

        const prevScale = this.scale;

        this.scale = newScale;

        const rect = this.wrapper.getBoundingClientRect();
        const centerX = this.scroller.scrollLeft + (rect.width / 2);
        const centerY = this.scroller.scrollTop + (rect.height / 2);

        const ratio = newScale / prevScale;

        this.content.style.transform = `scale(${newScale})`;

        this.scroller.scrollLeft = (centerX * ratio) - (rect.width / 2);
        this.scroller.scrollTop = (centerY * ratio) - (rect.height / 2);

        if (this.scrollbar) {
            this.scrollbar.setScale(newScale);
        }

        this.updateButtonsState();
    }

    /**
     * Инициализирует кнопки управления зумом
     */
    private initZoomButtons(): void {
        const zoomIn = this.wrapper.querySelector<HTMLElement>('.j-zoom-in');
        const zoomOut = this.wrapper.querySelector<HTMLElement>('.j-zoom-out');
        const reset = this.wrapper.querySelector<HTMLElement>('.j-zoom-reset');

        this.zoomButtons.zoomIn = zoomIn;
        this.zoomButtons.zoomOut = zoomOut;
        this.zoomButtons.reset = reset;

        zoomIn?.addEventListener('click', () => {
            this.zoomIn();
        });

        zoomOut?.addEventListener('click', () => {
            this.zoomOut();
        });

        reset?.addEventListener('click', () => {
            this.reset();
        });
    }

    /**
     * Обновляет состояние кнопок управления зумом
     */
    private updateButtonsState(): void {
        const isAtMin = this.isAtMinScale();
        const isAtMax = this.isAtMaxScale();

        if (this.zoomButtons.zoomIn) {
            if (isAtMax) {
                this.zoomButtons.zoomIn.classList.add(this.disabledClass);
            } else {
                this.zoomButtons.zoomIn.classList.remove(this.disabledClass);
            }
        }

        if (this.zoomButtons.zoomOut) {
            if (isAtMin) {
                this.zoomButtons.zoomOut.classList.add(this.disabledClass);
            } else {
                this.zoomButtons.zoomOut.classList.remove(this.disabledClass);
            }
        }
    }

    /**
     * Проверяет, достигнут ли минимальный масштаб
     * @returns {boolean} True, если текущий масштаб равен минимальному
     */
    private isAtMinScale(): boolean {
        return Math.abs(this.scale - this.min) < 0.001;
    }

    /**
     * Проверяет, достигнут ли максимальный масштаб
     * @returns {boolean} True, если текущий масштаб равен максимальному
     */
    private isAtMaxScale(): boolean {
        return Math.abs(this.scale - this.max) < 0.001;
    }
}
